/* Orrery — the analysis of a chart: what the visitor's birth data makes
   computable, and the structural facts read off the positions. One module,
   used by the page (assets/orrery.js renders it) and by tests/analysis.test.mjs
   (which recomputes everything from the Swiss Ephemeris reference and compares).

   Every number here is derived from the engine's chart. Nothing is typed.
   Policies, stated once and printed on the page by the renderer:
     - bodies counted: the ten planets (Sun to Pluto); nodes and angles are
       listed but never counted in elements, modalities, houses or patterns
     - aspects: between the ten planets, and each planet to the Ascendant and
       Midheaven when the angles are known; never angle to angle
     - orbs: conjunction, sextile, square, trine, opposition inside 8°;
       quincunx inside 3°
     - near a boundary: within 1° of a house cusp or a sign boundary

   Staging, driven by what is actually computable:
     stage 0  nothing yet
     stage 1  date known: the planets by sign, the Moon held (it moves ~13° a
              day), no houses or angles. A time without a place is still
              stage 1: the time zone is unknown until the place is.
     stage 2  date, time and place: everything.                            */
(function () {
  var ENGINE = window.OrreryEngine;
  var SIGN_NAMES = ENGINE.SIGN_NAMES;
  var ELEMENT = ['Fire', 'Earth', 'Air', 'Water'];
  var MODALITY = ['Cardinal', 'Fixed', 'Mutable'];
  var ASPECTS = [
    { name: 'conjunction', angle: 0, orb: 8, cls: 'hard', glyph: '☌' },
    { name: 'sextile', angle: 60, orb: 8, cls: 'soft', glyph: '⚹' },
    { name: 'square', angle: 90, orb: 8, cls: 'hard', glyph: '□' },
    { name: 'trine', angle: 120, orb: 8, cls: 'soft', glyph: '△' },
    { name: 'quincunx', angle: 150, orb: 3, cls: 'quincunx', glyph: '⚻' },
    { name: 'opposition', angle: 180, orb: 8, cls: 'hard', glyph: '☍' }
  ];
  var SYNODIC_MONTH = 29.530589;
  var norm = function (d) { return ((d % 360) + 360) % 360; };
  var sep = function (a, b) { var s = Math.abs(norm(a) - norm(b)); return s > 180 ? 360 - s : s; };
  var planets = function (chart) { return chart.bodies.filter(function (b) { return !b.node; }); };
  var signIdx = function (lon) { return Math.floor(norm(lon) / 30); };

  /* ---- staging: which chart these inputs allow, and nothing more
       0  nothing yet
       1  date: the planets by sign; the Moon held (it moves ~13° a day)
       2  date and time: the Moon, aspects between planets, patterns, the
          balance and the phase. Without a place the time zone is taken from
          this device and said so; the place corrects it.
       3  date, time and place: houses, angles, aspects to the angles, the reading */
  function stageFor(dob, tob, loc) {
    var located = !!(loc && loc.tz && isFinite(loc.lat) && isFinite(loc.lon));
    if (!dob) return { stage: 0, chart: null, missing: missingFor(0) };
    if (!tob) {
      // date only: the planets at local noon (or noon at the place if it is known); the Moon and angles withheld
      var part = ENGINE.chartFor(dob, '12:00', located ? loc : null);
      if (!part) return { stage: 0, chart: null, missing: missingFor(0) };
      part.stage = 1; part.moonKnown = false; part.anglesKnown = false; part.timeKnown = false; part.zoneAssumed = false;
      part.asc = 0; part.mc = 270; part.cusps = null;   // no horizon: the wheel is oriented with the first degree of the zodiac on the left
      return { stage: 1, chart: part, missing: missingFor(1, located) };
    }
    var chart = ENGINE.chartFor(dob, tob, located ? loc : null);
    if (!chart) return { stage: 0, chart: null, missing: missingFor(0) };
    if (located) { chart.stage = 3; chart.moonKnown = true; chart.anglesKnown = true; chart.zoneAssumed = false; return { stage: 3, chart: chart, missing: [] }; }
    chart.stage = 2; chart.moonKnown = true; chart.anglesKnown = false; chart.zoneAssumed = true;
    chart.asc = 0; chart.mc = 270; chart.cusps = null;
    return { stage: 2, chart: chart, missing: missingFor(2, false) };
  }
  // what the next stage needs, in the visitor's own terms; `what` names the section asking
  function missingFor(stage, located) {
    if (stage === 0) return [{ need: 'date', text: 'Enter your date of birth to draw your chart.' }];
    if (stage === 1) return [{ need: 'time', text: located ? 'Add your birth time to see the rest of your chart.' : 'Add your birth time to see the rest of your chart, and your birthplace for the houses.' }];
    return [{ need: 'place', text: 'Add your birthplace to see your houses and angles.' }];
  }
  function waitingFor(chart, what, needsPlace) {
    var stage = chart ? chart.stage : 0;
    if (stage === 0) return 'Enter your date of birth to draw your chart.';
    if (stage === 1) return 'Add your birth time to see ' + what + '.';
    if (stage === 2 && needsPlace) return 'Add your birthplace to see ' + what + '.';
    return '';
  }

  /* ---- the Moon that night */
  function moon(chart) {
    var ph = chart.lunarPhase;
    var day = ph.elongation / 360 * SYNODIC_MONTH;
    return { name: ph.name, elongation: ph.elongation, illuminated: ph.illuminated, day: day, month: SYNODIC_MONTH, waxing: ph.elongation < 180 };
  }

  /* ---- every aspect, tightest first */
  function aspects(chart) {
    var pts = planets(chart).map(function (b) { return { key: b.key, name: b.name, glyph: b.glyph, lon: b.lon, angle: false }; });
    if (chart.anglesKnown) pts.push({ key: 'asc', name: 'Ascendant', short: 'ASC', glyph: 'ASC', lon: chart.asc, angle: true }, { key: 'mc', name: 'Midheaven', short: 'MC', glyph: 'MC', lon: chart.mc, angle: true });
    var out = [];
    for (var i = 0; i < pts.length; i++) {
      for (var j = i + 1; j < pts.length; j++) {
        if (pts[i].angle && pts[j].angle) continue;
        var s = sep(pts[i].lon, pts[j].lon);
        for (var k = 0; k < ASPECTS.length; k++) {
          var A = ASPECTS[k], orb = Math.abs(s - A.angle);
          if (orb <= A.orb) out.push({ a: pts[i], b: pts[j], type: A.name, cls: A.cls, glyph: A.glyph, angle: A.angle, orb: orb });
        }
      }
    }
    out.sort(function (x, y) { return x.orb - y.orb; });
    return out;
  }

  /* ---- the twelve houses: cusp, planets inside, angular or not */
  function houses(chart) {
    if (!chart.cusps) return null;
    var occ = {};
    planets(chart).forEach(function (b) { var h = ENGINE.houseOf(chart.cusps, b.lon); (occ[h] = occ[h] || []).push(b); });
    var nodes = {};
    chart.bodies.filter(function (b) { return b.node; }).forEach(function (b) { var h = ENGINE.houseOf(chart.cusps, b.lon); (nodes[h] = nodes[h] || []).push(b); });
    return chart.cusps.map(function (c, i) {
      return { n: i + 1, cusp: c, sign: signIdx(c), planets: occ[i + 1] || [], nodes: nodes[i + 1] || [], angular: [1, 4, 7, 10].indexOf(i + 1) > -1 };
    });
  }

  /* ---- patterns: stelliums, grand trines, T-squares, grand crosses, yods */
  function patterns(chart, asp) {
    var P = planets(chart), byName = {};
    P.forEach(function (b) { byName[b.name] = b; });
    var has = function (x, y, type) { return asp.some(function (a) { return a.type === type && !a.a.angle && !a.b.angle && ((a.a.name === x && a.b.name === y) || (a.a.name === y && a.b.name === x)); }); };
    var found = [], absent = [];
    // stelliums: three or more planets in one sign
    var bySign = {};
    P.forEach(function (b) { (bySign[signIdx(b.lon)] = bySign[signIdx(b.lon)] || []).push(b); });
    Object.keys(bySign).forEach(function (s) {
      var group = bySign[s];
      if (group.length < 3) return;
      var lons = group.map(function (b) { return b.lon; });
      var spread = Math.max.apply(null, lons) - Math.min.apply(null, lons);
      var houseSet = chart.cusps ? group.map(function (b) { return ENGINE.houseOf(chart.cusps, b.lon); }).filter(function (v, i, a) { return a.indexOf(v) === i; }) : null;
      found.push({ kind: 'stellium', sign: Number(s), bodies: group, spread: spread, houses: houseSet });
    });
    // grand trines: three planets each trine the other two
    var trios = [], crosses = [];
    for (var i = 0; i < P.length; i++) for (var j = i + 1; j < P.length; j++) for (var k = j + 1; k < P.length; k++) {
      if (has(P[i].name, P[j].name, 'trine') && has(P[j].name, P[k].name, 'trine') && has(P[i].name, P[k].name, 'trine')) trios.push([P[i], P[j], P[k]]);
    }
    trios.forEach(function (t) { found.push({ kind: 'grand trine', bodies: t, element: ELEMENT[signIdx(t[0].lon) % 4] }); });
    // T-squares: an opposition with a third planet square to both ends; grand crosses: two such oppositions at right angles
    var opps = asp.filter(function (a) { return a.type === 'opposition' && !a.a.angle && !a.b.angle; });
    var tsq = [];
    opps.forEach(function (o) {
      P.forEach(function (c) {
        if (c.name === o.a.name || c.name === o.b.name) return;
        if (has(c.name, o.a.name, 'square') && has(c.name, o.b.name, 'square')) tsq.push({ opp: o, apex: c });
      });
    });
    for (var x = 0; x < opps.length; x++) for (var y = x + 1; y < opps.length; y++) {
      var o1 = opps[x], o2 = opps[y];
      if ([o1.a.name, o1.b.name].indexOf(o2.a.name) > -1 || [o1.a.name, o1.b.name].indexOf(o2.b.name) > -1) continue;
      if (has(o1.a.name, o2.a.name, 'square') && has(o1.a.name, o2.b.name, 'square') && has(o1.b.name, o2.a.name, 'square') && has(o1.b.name, o2.b.name, 'square')) crosses.push([o1.a, o1.b, o2.a, o2.b].map(function (p) { return byName[p.name]; }));
    }
    crosses.forEach(function (c) { found.push({ kind: 'grand cross', bodies: c, modality: MODALITY[signIdx(c[0].lon) % 3] }); });
    // a T-square that is part of a grand cross is the cross, not a separate figure
    tsq.filter(function (t) { return !crosses.some(function (c) { return c.some(function (p) { return p.name === t.apex.name; }) && c.some(function (p) { return p.name === t.opp.a.name; }); }); })
      .forEach(function (t) { found.push({ kind: 'T-square', bodies: [byName[t.opp.a.name], byName[t.opp.b.name], t.apex], apex: t.apex, modality: MODALITY[signIdx(t.apex.lon) % 3] }); });
    // yods: two planets in sextile, both quincunx a third (the apex)
    var yods = [];
    P.forEach(function (apex) {
      var legs = P.filter(function (p) { return p.name !== apex.name && has(apex.name, p.name, 'quincunx'); });
      for (var m = 0; m < legs.length; m++) for (var n = m + 1; n < legs.length; n++) if (has(legs[m].name, legs[n].name, 'sextile')) yods.push({ apex: apex, legs: [legs[m], legs[n]] });
    });
    yods.forEach(function (y) { found.push({ kind: 'yod', bodies: [y.legs[0], y.legs[1], y.apex], apex: y.apex }); });

    // what is not here, with the nearest miss where there is one
    var kinds = ['stellium', 'grand trine', 'T-square', 'grand cross', 'yod'];
    kinds.forEach(function (kind) {
      if (found.some(function (f) { return f.kind === kind; })) return;
      var why = '';
      if (kind === 'stellium') {
        var biggest = Object.keys(bySign).map(function (s) { return bySign[s]; }).sort(function (a, b) { return b.length - a.length; })[0];
        why = biggest && biggest.length === 2 ? biggest[0].name + ' and ' + biggest[1].name + ' share ' + SIGN_NAMES[signIdx(biggest[0].lon)] + ', but a stellium needs a third.' : 'No sign holds more than one planet.';
      } else if (kind === 'grand trine') {
        var tr = asp.filter(function (a) { return a.type === 'trine' && !a.a.angle && !a.b.angle; })[0];
        why = tr ? tr.a.name + ' and ' + tr.b.name + ' hold one side of it, but nothing sits at the third corner.' : 'No two planets are in trine.';
      } else if (kind === 'T-square') {
        why = opps.length ? (opps.length === 1 ? 'One opposition here, ' + opps[0].a.name + ' to ' + opps[0].b.name + ', but no planet squares both ends.' : opps.length + ' oppositions here, none with a planet squaring both ends.') : 'No two planets are in opposition.';
      } else if (kind === 'grand cross') {
        why = opps.length >= 2 ? 'Two oppositions here, but they do not cross at right angles.' : opps.length === 1 ? 'Would need two oppositions at right angles; there is one.' : 'Would need two oppositions at right angles; there are none.';
      } else if (kind === 'yod') {
        var qs = asp.filter(function (a) { return a.type === 'quincunx' && !a.a.angle && !a.b.angle; });
        why = qs.length >= 2 ? 'Quincunxes here, but no two of them share an apex with their other ends in sextile.' : qs.length === 1 ? 'One quincunx, ' + qs[0].a.name + ' to ' + qs[0].b.name + '; a yod needs two to one planet.' : 'No quincunxes inside three degrees.';
      }
      absent.push({ kind: kind, why: why });
    });
    return { found: found, absent: absent };
  }

  /* ---- the signature: elements, modalities, and three structural facts */
  function signature(chart, asp) {
    var P = planets(chart);
    var el = { Fire: [], Earth: [], Air: [], Water: [] }, mo = { Cardinal: [], Fixed: [], Mutable: [] };
    P.forEach(function (b) { var s = signIdx(b.lon); el[ELEMENT[s % 4]].push(b); mo[MODALITY[s % 3]].push(b); });
    var count = {};
    P.forEach(function (b) { count[b.name] = 0; });
    asp.forEach(function (a) { if (!a.a.angle) count[a.a.name]++; if (!a.b.angle) count[a.b.name]++; });
    var unaspected = P.filter(function (b) { return count[b.name] === 0; });
    var most = P.slice().sort(function (a, b) { return count[b.name] - count[a.name]; })[0];
    var runnerUp = P.filter(function (b) { return b.name !== most.name; }).sort(function (a, b) { return count[b.name] - count[a.name]; })[0];
    var quadrants = null;
    if (chart.cusps) {
      quadrants = [1, 2, 3, 4].map(function (q) {
        var hs = [3 * q - 2, 3 * q - 1, 3 * q];
        return { n: q, houses: hs, planets: P.filter(function (b) { return hs.indexOf(ENGINE.houseOf(chart.cusps, b.lon)) > -1; }) };
      });
    }
    return { elements: el, modalities: mo, counts: count, unaspected: unaspected, most: most, mostCount: count[most.name], runnerUp: runnerUp, runnerUpCount: runnerUp ? count[runnerUp.name] : 0, quadrants: quadrants, counted: P.length };
  }

  /* ---- near a boundary: within one degree of a house cusp or a sign boundary */
  function nearCusp(chart) {
    var out = [];
    planets(chart).forEach(function (b) {
      if (!chart.moonKnown && b.key === 'moon') return;
      var inSign = norm(b.lon) % 30;
      if (inSign < 1) out.push({ body: b, kind: 'sign', dist: inSign, side: 'after', from: SIGN_NAMES[(signIdx(b.lon) + 11) % 12], to: SIGN_NAMES[signIdx(b.lon)] });
      else if (30 - inSign < 1) out.push({ body: b, kind: 'sign', dist: 30 - inSign, side: 'before', from: SIGN_NAMES[signIdx(b.lon)], to: SIGN_NAMES[(signIdx(b.lon) + 1) % 12] });
      if (chart.cusps) {
        for (var h = 0; h < 12; h++) {
          var d = norm(b.lon - chart.cusps[h]);
          if (d < 1) out.push({ body: b, kind: 'house', dist: d, side: 'after', from: (h + 11) % 12 + 1, to: h + 1 });
          else if (d > 359) out.push({ body: b, kind: 'house', dist: 360 - d, side: 'before', from: h + 1, to: (h + 1) % 12 + 1 });
        }
      }
    });
    return out;
  }

  function analyse(chart) {
    if (!chart) return null;
    var asp = chart.moonKnown ? aspects(chart) : null;
    return {
      stage: chart.stage,
      moon: chart.moonKnown ? moon(chart) : null,
      aspects: asp,
      houses: chart.anglesKnown || chart.solarHouses ? houses(chart) : null,   // solar houses (no birth time) are still houses
      patterns: asp ? patterns(chart, asp) : null,
      signature: asp ? signature(chart, asp) : null,
      nearCusp: nearCusp(chart)
    };
  }

  window.OrreryAnalysis = { stageFor: stageFor, waitingFor: waitingFor, analyse: analyse, aspects: aspects, houses: houses, patterns: patterns, signature: signature, nearCusp: nearCusp, moon: moon, ASPECTS: ASPECTS, ELEMENT: ELEMENT, MODALITY: MODALITY, SYNODIC_MONTH: SYNODIC_MONTH, VERSION: 1 };
})();
